import { useEffect } from 'react'
import { useRouter } from 'next/router'
import Experiment, { useBucket } from '@mc/experiments-react'
import Nav from '@mc/navigation'
import { paths } from '@mc/paths'
import { Product, trackExperimentViewed } from '@mc/track-event'
import Image from 'components/LegacyImage'
import Link from 'components/Link'
import {
  EnterpriseMobileMenuVariant1,
  EnterpriseMobileMenuVariant2,
  EnterpriseMobileMenuVariant3,
  MCCERTS_NAVIGATION_TESTING_PRE_PAYWALL_EXP_FLAG,
  useIsMcCerts280Variant2,
  useIsMcCerts280Variant3,
  useIsMcCerts280VariantControl,
  TopNavigation,
} from 'experiments/mccerts-280_v1'
import { AdminLink } from '../shared/AdminLink'
import { LoginMenu } from '../shared/LoginMenu'
import { BrowseSkillsLibraryCta } from './BrowseSkillsLibraryCta'
import { HowItWorksCta } from './HowItWorksCta'
import { MobileMenu } from './MobileMenu'
import { NavActions } from './NavActions'
import { trackMAWLogoClick } from './utils'

const NEW_FOR_BUSINESS_PAGE = 'web_flw-194_new_for_business_page_v1'
export const useIsNewForBusinessPageEnabled = () =>
  useBucket(NEW_FOR_BUSINESS_PAGE) === 'variant_1'

const EnterpriseNavigation = () => {
  const { pathname, query } = useRouter()
  const showBrowseLibraryCTA = (
    [
      paths.enterpriseHomepage,
      paths.enterpriseCart,
      paths.enterpriseHowItWorks,
    ] as string[]
  ).includes(pathname)
  const isMcCerts280VariantControlEnabled = useIsMcCerts280VariantControl()
  const isNewForBusinessPageEnabled = useIsNewForBusinessPageEnabled()
  const isMcCerts280Variant2Enabled = useIsMcCerts280Variant2()
  const isMcCerts280Variant3Enabled = useIsMcCerts280Variant3()

  const logoUrl =
    pathname === paths.enterpriseHomepage
      ? paths.root
      : paths.enterpriseHomepage

  useEffect(() => {
    // Track the experiment viewed event for the control variation
    if (isMcCerts280VariantControlEnabled) {
      trackExperimentViewed(MCCERTS_NAVIGATION_TESTING_PRE_PAYWALL_EXP_FLAG)
    }
  }, [isMcCerts280VariantControlEnabled])

  return (
    <>
      {isMcCerts280Variant2Enabled && (
        <TopNavigation
          className='mc-py-1'
          contained
          product={Product.AT_WORK}
          showAtWorkLink
        />
      )}
      {isMcCerts280Variant3Enabled && (
        <TopNavigation
          className='mc-py-1'
          contained
          showOnCallLink
          showAtWorkLink
          product={Product.AT_WORK}
        />
      )}
      <Nav
        background='solid'
        className='mc-page__header'
        data-testid='enterprise-navbar'
      >
        <div className='col-6 col-sm-auto d-flex align-items-center'>
          <Link
            className='d-flex align-items-center mc-mr-4'
            href={logoUrl}
            onClick={trackMAWLogoClick({
              logoUrl,
              pathname,
              query,
            })}
            isBasic
          >
            <Image
              src='https://static.masterclass.com/mc-enterprise-logo.png'
              alt='MasterClass Logo'
              width={173}
              height={24}
            />
          </Link>
          {showBrowseLibraryCTA && (
            <div className='d-flex'>
              <div className='d-none d-md-block'>
                <HowItWorksCta
                  textCTAKey='nav.cta.howItWorks'
                  className='mc-py-0'
                />
              </div>
              <div className='d-none d-md-block'>
                <BrowseSkillsLibraryCta
                  textCTAKey='nav.cta.browseSkillsLibrary'
                  className='mc-py-0'
                />
              </div>
            </div>
          )}
        </div>

        <Nav.Right>
          <div className='d-none d-md-block'>
            <AdminLink />
          </div>

          <LoginMenu>
            <Experiment
              name={MCCERTS_NAVIGATION_TESTING_PRE_PAYWALL_EXP_FLAG}
              control={() => (
                <MobileMenu
                  newForBusinessPageEnabled={isNewForBusinessPageEnabled}
                />
              )}
              variant_1={() => (
                <EnterpriseMobileMenuVariant1
                  newForBusinessPageEnabled={isNewForBusinessPageEnabled}
                />
              )}
              variant_2={() => (
                <EnterpriseMobileMenuVariant2
                  newForBusinessPageEnabled={isNewForBusinessPageEnabled}
                />
              )}
              variant_3={() => (
                <EnterpriseMobileMenuVariant3
                  newForBusinessPageEnabled={isNewForBusinessPageEnabled}
                />
              )}
            />
          </LoginMenu>

          <div className='d-none d-sm-block'>
            <NavActions />
          </div>
        </Nav.Right>
      </Nav>
    </>
  )
}

export default EnterpriseNavigation
